import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { AdvisoryFee, CustomAdvisoryFee, CustomizeAdvisoryFeeDialogComponent } from '@ddt-web/advisory-fee';
import { ApplicationPathConstants, ErrorDCR, ViewCategory, VisibilityType } from '@ddt-web/config';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { SessionTimeoutService } from '@ddt-web/core';
import { DisplayDataFactoryService, ViewDataFactoryService } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { ErrorPopupService, PopupDialogAction, PopupDialogActionOptions, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { ModelsBrowseDataService, ModelsBrowseStoreConfig, ModelsBrowseStoreHelperService } from '@ddt-web/models-browsing';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { FeeQueryParamKey } from '@ddt-web/url';
import { PartnerUserProfileService } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';

import { ME_VISIBILITY_CONFIG } from './config';

@Component({
	selector: 'me-models-experience-root',
	templateUrl: './models-experience.component.html',
	styleUrls: ['./models-experience.component.scss']
})
export class ModelsExperienceComponent implements OnInit {

	@InjectQueryParam(FeeQueryParamKey.CUSTOM_FEE) private readonly customFee: string;
	@InjectQueryParam(FeeQueryParamKey.EXCLUDE_FEE) private readonly excludeFee: string;
	@InjectConfig(ModelsExperienceConfigKey.DROP_DOWN_FILTERS) private readonly dropDownFilters: Array<{ [key: string]: string }> = [];
	@InjectConfig(SharedConfigKey.CUSTOM_ADVISORY_FEE) private readonly customAdvisoryFee: AdvisoryFee = CustomAdvisoryFee;
	@InjectConfig(SharedConfigKey.SHOULD_LOAD_MODEL_FAMILIES_FROM_IRETIRE_SERVER) private readonly shouldLoadModelFamiliesFromIretireServer: boolean = true;
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly enableCustomFee: boolean = true;

	constructor(
		private displayDataService: DisplayDataFactoryService,
		private partnerUserProfileService: PartnerUserProfileService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private applicationStateProxyService: ApplicationStateStoreProxyService,
		private viewDataFactoryService: ViewDataFactoryService,
		private router: Router,
		private dialogService: DialogService,
		private contentService: ContentService,
		private modelsBrowseStoreHelperService: ModelsBrowseStoreHelperService,
		private sessionTimeoutService: SessionTimeoutService,
		protected errorPopupService: ErrorPopupService,
		private route: ActivatedRoute,
		private localeUtilResolverService: LocaleUtilResolverService
	) { }

	public title = 'models-experience';
	public appPaths = ApplicationPathConstants.MODELS_EXPERIENCE_PATHS;
	public visibilityType: VisibilityType = VisibilityType.PUBLIC_VIEW;

	public async ngOnInit(): Promise<void> {
		const isCwpUserSignedIn = get(window, 'BLK.isCwpUserSignedIn', false);
		this.router.events.pipe(first(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			if (this.enableCustomFee) {
				if (isEmpty(this.customFee) && isEmpty(this.excludeFee)) {
					this.applicationStateProxyService.updateCustomAdvisoryFee({ customAdvisoryFee: this.customAdvisoryFee?.defaultFee, includeFee: true } as PerformanceState);
				}
				this.dialogService.open(CustomizeAdvisoryFeeDialogComponent, { data: { isEditMode: false } });
			}
		});
		await this.setupDataLayer(isCwpUserSignedIn);
		if (isCwpUserSignedIn) {
			this.redirectToUrl();
		}
	}

	private async setupDataLayer(isCwpUserSignedIn?: boolean): Promise<void> {

		if (isCwpUserSignedIn) {
			await this.checkUserAuthorization();
			this.partnerUserProfileService.isPartnerUser().pipe(first()).subscribe(isPartnerUser => {
				this.displayDataService.isPartnerUser = isPartnerUser;
			});

		}
		this.displayDataService.initialise(ViewCategory.MODELS_EXPERIENCE, ME_VISIBILITY_CONFIG, this.visibilityType);

		this.viewDataFactoryService.registerAppStoreConfig(ModelsBrowseStoreConfig);
		const isPublicView = this.visibilityType === VisibilityType.PUBLIC_VIEW;
		if (this.shouldLoadModelFamiliesFromIretireServer) {
			this.modelsBrowseDataService.fetchOldFamilyList(ModelsBrowseStoreConfig.families);
		} else {
			this.modelsBrowseDataService.fetchFamilyList(ModelsBrowseStoreConfig.families, isPublicView);
		}

		this.modelsBrowseStoreHelperService.updateStoreFromResponseList(ModelsBrowseStoreConfig.filterConfig, this.dropDownFilters, 'meFilterId');
	}

	private redirectToUrl(): void {
		this.route.queryParams.pipe(first(queryParams => !isEmpty(queryParams))).subscribe((queryParams) => {
			const redirectUrl = queryParams['redirectUrlPath'] ?? '';
			if (['state', 'code', 'nonce', 'code_verifier', 'redirectUrlPath', 'triggerSignIn'].some(param => queryParams[param])) {
				this.router.navigate([redirectUrl], {
					queryParams: {
						'state': null,
						'code': null,
						'nonce': null,
						'code_verifier': null,
						'triggerSignIn': null,
						'redirectUrlPath': null
					},
					relativeTo: this.route,
					queryParamsHandling: 'merge',
					replaceUrl: true // remove the params without adding to history
				});
			}
		});
	}

	// this is a tactical solution to limit access to internal users and Verified FPs
	// TODO: to find a way to strategically address this unique use case
	private async checkUserAuthorization(): Promise<void> {
		let isAuthorized: boolean;
		const locale = this.localeUtilResolverService.getResolvedLocaleValue();
		if (locale === 'au') {
			isAuthorized = true; // Australia model doesn't have a public view so we assume that user level authentication is already done at the CWP page before the app container loads whereas US has a public view.
		} else {
			try {
				isAuthorized = await this.isBFAUser$().toPromise();
			} catch (err) {
				console.error(err);
				isAuthorized = false;
			}
		}
		if (isAuthorized) {
			this.visibilityType = VisibilityType.LOGGED_IN_VIEW;
		} else {
			// revert the authentication check
			this.sessionTimeoutService.revertAuthenticationCheck();
			const dialogErrorMessage = PopupDialogObject.create({
				description: this.contentService.get('PAGE_ACCESS_MISSING_DESC_1', ErrorDCR.GENERAL_CONFIG_ERROR)
			} as PopupDialogOptions);
			dialogErrorMessage.actions = [
				PopupDialogAction.create({
					description: this.contentService.get('PAGE_ACCESS_MISSING_DESC_2', ErrorDCR.DOMAIN_ACCESS_NOT_ALLOWED_ERROR),
					label: this.contentService.get('CLOSE_DIALOG_BTN', 'Close')
				} as PopupDialogActionOptions)
			];
			this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, false);
		}
	}

	private isBFAUser$(): Observable<boolean> {
		const isBFAUser = get(window, 'BLK.isBFA', false);
		return of(isBFAUser);
	}
}
